import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Calendar, Clock, Ticket, Popcorn, Sparkles, ArrowRight, Mail, Film,
  Play, X, PartyPopper, ChevronDown, Gift, HelpCircle, Quote, Video, Loader2, User, Layers, Star,
} from "lucide-react";
import CinemaBrand from "@/components/CinemaBrand";
import TriviaWidget from "@/components/TriviaWidget";
import PinGate from "@/components/PinGate";
import EventBookingBlock from "@/components/EventBookingBlock";
import { QRCodeCanvas } from "qrcode.react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Event-Type → Label + Farbe für Event-Badges (G) — muss zu backend constants matchen
const EVENT_STYLES = {
  marvel_night: { label: "Marvel Night", cls: "bg-red-600/90 text-white" },
  hp_weekend: { label: "Harry Potter", cls: "bg-amber-700/90 text-white" },
  sw_marathon: { label: "Star Wars", cls: "bg-yellow-500/90 text-black" },
  horror_night: { label: "Horror", cls: "bg-red-900/90 text-white" },
  weihnachtskino: { label: "Weihnachten", cls: "bg-emerald-700/90 text-white" },
  disney_family: { label: "Disney", cls: "bg-sky-600/90 text-white" },
  gaming_night: { label: "Gaming", cls: "bg-purple-700/90 text-white" },
};

export default function Landing() {
  const [data, setData] = useState(null);
  const [heroIdx, setHeroIdx] = useState(0);
  const [trailer, setTrailer] = useState(null);
  const [quoteIdx, setQuoteIdx] = useState(0);
  const [marathonOrderFor, setMarathonOrderFor] = useState(null);
  const [voucherDlg, setVoucherDlg] = useState(null);  // {amount}
  const [packDlg, setPackDlg] = useState(null);        // {count, price}
  const [movieDetail, setMovieDetail] = useState(null); // movie obj
  const [seriesDetail, setSeriesDetail] = useState(null); // series id
  const [gateChecked, setGateChecked] = useState(false); // PIN-Gate initial-Check fertig?
  const [gateNeedsPin, setGateNeedsPin] = useState(false); // PIN erforderlich & noch nicht freigeschaltet?

  const loadLanding = React.useCallback(async () => {
    const pin = localStorage.getItem("mh_landing_pin") || "";
    try {
      const r = await axios.get(`${API}/landing`, {
        headers: pin ? { "X-Landing-Pin": pin } : {},
      });
      setData(r.data);
      setGateNeedsPin(false);
    } catch (e) {
      if (e?.response?.status === 401) {
        // gespeicherter PIN nicht mehr gültig → löschen & Gate zeigen
        localStorage.removeItem("mh_landing_pin");
        setGateNeedsPin(true);
      } else {
        setData({});
      }
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data: st } = await axios.get(`${API}/public/pin-status`);
        const stored = localStorage.getItem("mh_landing_pin") || "";
        if (st.required && !stored) {
          setGateNeedsPin(true);
          setGateChecked(true);
          return;
        }
        setGateChecked(true);
        await loadLanding();
      } catch {
        setGateChecked(true);
        await loadLanding();
      }
    })();
  }, [loadLanding]);

  const handleGateSuccess = async (pin) => {
    localStorage.setItem("mh_landing_pin", pin);
    setGateNeedsPin(false);
    await loadLanding();
  };

  // Hero-Rotation
  const heroFilms = useMemo(
    () => (data?.current_movies || []).filter((m) => m.backdrop_url).slice(0, 5),
    [data],
  );
  useEffect(() => {
    if (heroFilms.length < 2) return;
    const t = setInterval(() => setHeroIdx((i) => (i + 1) % heroFilms.length), 8000);
    return () => clearInterval(t);
  }, [heroFilms.length]);

  const activeHero = heroFilms[heroIdx] || heroFilms[0];

  // Finde die nächste Vorstellung des aktiven Hero-Films (für „Ticket sichern"-CTA)
  const heroNextShow = useMemo(() => {
    if (!activeHero?.id) return null;
    return (data?.upcoming || []).find(
      (s) => s.movie_id === activeHero.id || s.movie?.id === activeHero.id,
    );
  }, [activeHero, data?.upcoming]);

  // Gruppieren Vorstellungen nach Datum
  const byDate = useMemo(() => {
    const m = new Map();
    for (const st of data?.upcoming || []) {
      if (!m.has(st.date)) m.set(st.date, []);
      m.get(st.date).push(st);
    }
    return Array.from(m.entries()).slice(0, 7);
  }, [data]);

  const testimonials = data?.testimonials || [];
  const faq = data?.faq || [];
  const voucherAmounts = data?.voucher_amounts || [];
  // Filme mit Trailer für die „Trailer der Woche"-Sektion
  const trailerMovies = useMemo(() => {
    const pool = [...(data?.current_movies || []), ...(data?.coming_soon || [])];
    const seen = new Set();
    return pool.filter((m) => {
      if (!m.trailer_id || seen.has(m.id)) return false;
      seen.add(m.id);
      return true;
    }).slice(0, 8);
  }, [data]);

  // PIN-Gate: solange PIN nötig & nicht eingegeben → nur Wall zeigen, sonst nichts leaken.
  if (gateChecked && gateNeedsPin) {
    return <PinGate onSuccess={handleGateSuccess} />;
  }

  return (
    <div className="min-h-screen bg-[#0A0806] text-[#F5F0E6] font-body">
      {/* Papier-Vignette */}
      <div className="fixed inset-0 pointer-events-none opacity-[0.05] mix-blend-overlay"
        style={{ backgroundImage: "radial-gradient(#D4AF37 1px, transparent 1px)", backgroundSize: "3px 3px" }} />

      {/* Nav */}
      <nav className="relative z-20 flex items-center justify-between px-6 md:px-14 py-6 border-b border-[#1A1A1A] flex-wrap gap-4">
        <CinemaBrand size="md" stacked />
        <div className="flex items-center gap-3 md:gap-4 flex-wrap">
          <a href="#programm" className="text-[10px] uppercase tracking-[0.4em] text-[#A8A398] hover:text-[#D4AF37]">Programm</a>
          <a href="#bald" className="text-[10px] uppercase tracking-[0.4em] text-[#A8A398] hover:text-[#D4AF37]">Bald</a>
          <a href="#snack" className="text-[10px] uppercase tracking-[0.4em] text-[#A8A398] hover:text-[#D4AF37]">Snacks</a>
          <a href="#gutschein" className="text-[10px] uppercase tracking-[0.4em] text-[#A8A398] hover:text-[#D4AF37]">Gutscheine</a>
          <a href="#club" className="text-[10px] uppercase tracking-[0.4em] text-[#A8A398] hover:text-[#D4AF37]">Club</a>
          <a href="#filmreihen" className="text-[10px] uppercase tracking-[0.4em] text-[#A8A398] hover:text-[#D4AF37]">Reihen</a>
          <a href="#trivia" className="text-[10px] uppercase tracking-[0.4em] text-[#A8A398] hover:text-[#D4AF37]">Trivia</a>
          <a href="#faq" className="text-[10px] uppercase tracking-[0.4em] text-[#A8A398] hover:text-[#D4AF37]">FAQ</a>
          <Link to="/shop" data-testid="landing-shop-cta"
            className="text-[10px] uppercase tracking-[0.4em] text-[#A8A398] hover:text-[#D4AF37]">
            Shop
          </Link>
          <Link to="/mitglied/login" data-testid="landing-member-login"
            className="text-[10px] uppercase tracking-[0.4em] px-3 py-2 border border-[#8B7328] text-[#EEDC82] rounded-full hover:bg-[#D4AF37]/10 hover:border-[#D4AF37] transition-colors">
            <User size={11} className="inline mr-1" /> Mitglied
          </Link>
          <Link to="/kiosk" data-testid="landing-kiosk-cta"
            className="text-[10px] uppercase tracking-[0.4em] px-4 py-2 border border-[#D4AF37] text-[#D4AF37] rounded-full hover:bg-[#D4AF37] hover:text-black transition-colors">
            <Ticket size={11} className="inline mr-1" /> Ticket-Automat
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative h-[70vh] min-h-[520px] overflow-hidden">
        {heroFilms.map((m, i) => (
          <div key={m.id || i}
            className={`absolute inset-0 transition-opacity duration-[1400ms] ${i === heroIdx ? "opacity-100" : "opacity-0"}`}
            style={{ backgroundImage: `url(${m.backdrop_url})`, backgroundSize: "cover", backgroundPosition: "center" }} />
        ))}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0806] via-[#0A0806]/70 to-[#0A0806]/40" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0806] via-transparent to-transparent" />

        <div className="relative z-10 h-full max-w-[1600px] mx-auto px-6 md:px-14 flex flex-col justify-end pb-16">
          <div className="text-[11px] uppercase tracking-[0.5em] text-[#D4AF37] animate-fade-up">Aktuelles Programm</div>
          <h1 className="font-heading text-4xl sm:text-5xl md:text-7xl lg:text-8xl mt-4 max-w-3xl leading-[0.95] animate-fade-up" style={{ animationDelay: "80ms" }}>
            {activeHero?.title_de || activeHero?.title_original || "Willkommen im Kino."}
          </h1>
          {activeHero?.genre && (
            <div className="mt-4 text-[10px] uppercase tracking-[0.4em] text-[#A8A398] animate-fade-up" style={{ animationDelay: "160ms" }}>
              {activeHero.genre} · {activeHero.duration_min ? `${activeHero.duration_min} Min` : ""} · {(activeHero.age_rating || activeHero.rating || "").toString().replace(/^FSK\s*/i, "FSK ") || "FSK ?"}
            </div>
          )}
          {(activeHero?.synopsis || activeHero?.description) && (
            <p className="mt-6 max-w-2xl text-sm md:text-base text-[#F5F0E6]/80 line-clamp-3 animate-fade-up" style={{ animationDelay: "220ms" }}>
              {activeHero.synopsis || activeHero.description}
            </p>
          )}
          <div className="mt-8 flex items-center gap-3 flex-wrap animate-fade-up" style={{ animationDelay: "280ms" }}>
            {heroNextShow ? (
              <Link to={`/kiosk/ticket/${heroNextShow.id}`} data-testid="landing-hero-cta"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#D4AF37] text-black rounded-full text-xs uppercase tracking-[0.4em] hover:bg-[#EEDC82] transition-colors">
                Ticket sichern <ArrowRight size={14} />
              </Link>
            ) : (
              <button onClick={() => setMovieDetail(activeHero)} data-testid="landing-hero-cta"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#D4AF37] text-black rounded-full text-xs uppercase tracking-[0.4em] hover:bg-[#EEDC82] transition-colors">
                Details ansehen <ArrowRight size={14} />
              </button>
            )}
            {activeHero?.trailer_id && (
              <button
                onClick={() => setTrailer({ ytId: activeHero.trailer_id, title: activeHero.title_de || activeHero.title_original })}
                data-testid="landing-hero-trailer"
                className="inline-flex items-center gap-2 px-6 py-3 border border-[#F5F0E6]/30 text-[#F5F0E6] rounded-full text-xs uppercase tracking-[0.4em] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors">
                <Play size={14} /> Trailer ansehen
              </button>
            )}
            {heroFilms.length > 1 && (
              <div className="flex gap-1.5 ml-2">
                {heroFilms.map((_, i) => (
                  <button key={i} onClick={() => setHeroIdx(i)} aria-label={`Slide ${i + 1}`}
                    className={`h-[3px] rounded-full transition-all ${i === heroIdx ? "w-10 bg-[#D4AF37]" : "w-4 bg-[#F5F0E6]/30"}`} />
                ))}
              </div>
            )}
          </div>
          <NextShowCountdown upcoming={data?.upcoming || []} />
        </div>
      </section>

      {/* Aktions-Banner (C) */}
      {data?.promo?.active && <PromoBanner promo={data.promo} />}

      {/* Wochenprogramm — Poster-Grid */}
      <section id="programm" className="relative py-20 px-6 md:px-14 max-w-[1600px] mx-auto">
        <SectionHead eyebrow="Wochenprogramm" title="Diese Woche im Kino" />
        {byDate.length === 0 ? (
          <EmptyBlock icon={<Calendar size={32} />} text="Aktuell keine geplanten Vorstellungen" />
        ) : (
          <div className="space-y-10 mt-10">
            {byDate.map(([date, shows]) => <DayBlock key={date} isoDate={date} shows={shows} />)}
          </div>
        )}
      </section>

      {/* Aktuelle Filme */}
      {(data?.current_movies || []).length > 0 && (
        <section className="relative py-16 px-6 md:px-14 max-w-[1600px] mx-auto border-t border-[#1A1A1A]">
          <SectionHead eyebrow="Aktuell" title="Filme im Kino" />
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mt-10">
            {data.current_movies.slice(0, 12).map((m) => <MovieCard key={m.id} movie={m} onTrailer={setTrailer} onOpen={setMovieDetail} />)}
          </div>
        </section>
      )}

      {/* Bald im Kino */}
      {(data?.coming_soon || []).length > 0 && (
        <section id="bald" className="relative py-16 px-6 md:px-14 max-w-[1600px] mx-auto border-t border-[#1A1A1A]">
          <SectionHead eyebrow="Vorschau" title="Bald im Kino" />
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mt-10" data-testid="landing-coming-soon">
            {data.coming_soon.map((m) => <MovieCard key={m.id} movie={m} soon onTrailer={setTrailer} onOpen={setMovieDetail} />)}
          </div>
        </section>
      )}

      {/* Filmreihen — nur bei Vorhandensein */}
      {(data?.series || []).length > 0 && (
        <section id="filmreihen" className="relative py-16 px-6 md:px-14 max-w-[1600px] mx-auto border-t border-[#1A1A1A]">
          <SectionHead eyebrow="Sagas & Universen" title="Filmreihen bei uns" />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-10" data-testid="landing-series">
            {data.series.map((s) => (
              <button key={s.id} onClick={() => setSeriesDetail(s.id)} className="group text-left rounded-lg overflow-hidden border transitions-safe hover:translate-y-[-2px] w-full"
                data-testid={`landing-series-${s.id}`}
                style={{ borderColor: (s.color || "#D4AF37") + "40", background: "linear-gradient(180deg, #0A0A0A 0%, #060606 100%)" }}>
                <div className="relative aspect-[16/9] bg-[#050505]">
                  {(s.backdrop_url || s.poster_url) ? (
                    <img src={s.backdrop_url || s.poster_url} alt={s.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  ) : null}
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
                  {s.marathon_price != null && s.marathon_price > 0 && (
                    <div className="absolute top-3 right-3 bg-[#D4AF37] text-black text-[10px] uppercase tracking-widest px-3 py-1 rounded-full font-heading shadow-lg">
                      Marathon {Number(s.marathon_price).toFixed(0)} €
                    </div>
                  )}
                  <div className="absolute bottom-0 left-0 right-0 p-5">
                    <div className="text-[10px] uppercase tracking-[0.35em] font-heading" style={{ color: s.color || "#D4AF37" }}>Filmreihe</div>
                    <h3 className="font-heading text-2xl md:text-3xl text-[#F5F0E6] mt-1 leading-tight">{s.name}</h3>
                    {s.subtitle && <div className="text-xs md:text-sm text-[#A8A398] mt-1">{s.subtitle}</div>}
                  </div>
                </div>
                {(s.description || (s.movies || []).length > 0) && (
                  <div className="p-5 space-y-3">
                    {s.description && <p className="text-xs md:text-sm text-[#A8A398] leading-relaxed line-clamp-3">{s.description}</p>}
                    {(s.movies || []).length > 0 && (
                      <div className="flex -space-x-3 pt-2">
                        {s.movies.slice(0, 6).map((m) => (
                          <div key={m.id} className="relative w-10 h-14 rounded overflow-hidden border-2 border-[#0A0A0A] flex-shrink-0"
                            style={{ background: "#050505" }} title={m.title_de}>
                            {m.poster_url ? <img src={m.poster_url} alt="" className="w-full h-full object-cover" /> : null}
                          </div>
                        ))}
                        {s.movies.length > 6 && (
                          <div className="w-10 h-14 rounded border-2 border-[#0A0A0A] bg-[#0A0A0A] flex items-center justify-center text-[10px] font-heading text-[#D4AF37]">
                            +{s.movies.length - 6}
                          </div>
                        )}
                      </div>
                    )}
                    <div className="flex items-center justify-between pt-3 border-t border-[#1A1A1A] text-[10px] uppercase tracking-widest">
                      <span className="text-[#575148]">{(s.movies || []).length} Filme</span>
                      <span className="text-[#EEDC82] font-heading group-hover:translate-x-1 transition-transform" style={{ color: s.color || "#EEDC82" }}>
                        {s.upcoming_shows > 0 ? `${s.upcoming_shows} Vorstellung${s.upcoming_shows === 1 ? "" : "en"} →` : "Details ansehen →"}
                      </span>
                    </div>
                  </div>
                )}
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Trailer der Woche — Auto-Rotation */}
      {trailerMovies.length > 0 && <TrailerOfWeek movies={trailerMovies} />}

      {/* Zitate-Karussell (E) */}
      {testimonials.length > 0 && (
        <section className="relative py-20 px-6 md:px-14 border-t border-[#1A1A1A] bg-gradient-to-b from-[#0A0806] to-[#120E08]">
          <div className="max-w-3xl mx-auto text-center">
            <SectionHead eyebrow="Stimmen" title="Was Gäste sagen" center icon={<Quote className="text-[#D4AF37]" size={22} />} />
            <TestimonialCarousel items={testimonials} idx={quoteIdx} setIdx={setQuoteIdx} />
          </div>
        </section>
      )}

      {/* Snack-Teaser */}
      {(data?.products || []).length > 0 && (
        <section id="snack" className="relative py-20 px-6 md:px-14 border-t border-[#1A1A1A]">
          <div className="max-w-[1600px] mx-auto">
            <SectionHead eyebrow="Snack Bar" title="Unsere Snackbar" icon={<Popcorn className="text-[#D4AF37]" size={22} />} />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-10" data-testid="landing-snack-teaser">
              {data.products.slice(0, 8).map((p) => (
                <div key={p.id} className="border border-[#2A2A2A] rounded-lg p-4 hover:border-[#D4AF37]/50 transition-colors bg-black/40">
                  <div className="text-3xl mb-2">{p.emoji || "🍿"}</div>
                  <div className="font-heading text-lg leading-tight">{p.name}</div>
                  <div className="text-[10px] uppercase tracking-[0.32em] text-[#575148] mt-1">{p.category || ""}</div>
                  <div className="mt-2 text-[#D4AF37] font-mono">{Number(p.sell_price ?? p.price ?? 0).toFixed(2)} €</div>
                </div>
              ))}
            </div>
            <div className="mt-6 text-[10px] uppercase tracking-[0.4em] text-[#575148]">Bestellung direkt am Ticket-Automat oder beim Personal</div>
          </div>
        </section>
      )}

      {/* Gutscheine & 10er-Karten leben jetzt exklusiv im Shop */}
      {voucherAmounts.length > 0 && (
        <section id="gutschein" className="relative py-16 px-6 md:px-14 border-t border-[#1A1A1A]">
          <div className="max-w-2xl mx-auto text-center">
            <SectionHead eyebrow="Gutscheine &amp; Sparabo" title="Verschenke Kino" center icon={<Gift className="text-[#D4AF37]" size={22} />} />
            <p className="mt-6 text-sm md:text-base text-[#F5F0E6]/80">
              Gutscheine, 10er-Kartenpakete und mehr — bequem im Shop mit online-Zahlung oder Barzahlung an der Kasse.
            </p>
            <a href="/shop" data-testid="landing-shop-cta"
              className="mt-8 inline-flex items-center gap-3 px-8 py-4 border-2 border-[#D4AF37] rounded-full bg-[#D4AF37]/10 hover:bg-[#D4AF37] hover:text-black text-[#D4AF37] text-[11px] uppercase tracking-[0.4em] transition-colors">
              <Ticket size={16} /> Zum Shop
            </a>
          </div>
        </section>
      )}

      {/* Club & Newsletter */}
      <section id="club" className="relative py-20 px-6 md:px-14 border-t border-[#1A1A1A]">
        <div className="max-w-3xl mx-auto text-center">
          <SectionHead eyebrow="Movie House Club" title="Werde Teil des Clubs" center icon={<Sparkles className="text-[#D4AF37]" size={22} />} />
          <p className="mt-6 text-sm md:text-base text-[#F5F0E6]/80 max-w-xl mx-auto">
            Newsletter mit exklusiven Vorpremieren, persönliche Club-Karte mit deinem Lieblingsfilm-Backdrop und Geburtstags-Überraschungen.
          </p>
          <div className="mt-10"><ClubSignupForm /></div>
        </div>
      </section>

      {/* Movie House Abo — Monatskarten */}
      <section id="abo" className="relative py-20 px-6 md:px-14 border-t border-[#1A1A1A] bg-gradient-to-b from-[#0A0806] to-[#120E08]" data-testid="landing-abo">
        <div className="max-w-5xl mx-auto">
          <SectionHead eyebrow="Movie House Abo" title="Monatskarte für Filmfans" center icon={<Ticket className="text-[#D4AF37]" size={22} />} />
          <div className="mt-8 grid md:grid-cols-3 gap-4">
            {(data?.subscription_plans || []).map((p) => {
              const highlight = p.tier === "duo";
              return (
                <div key={p.tier} data-testid={`landing-abo-${p.tier}`}
                  className={`card-lux p-6 flex flex-col relative overflow-hidden ${highlight ? "ring-1 ring-[#D4AF37]/50" : ""}`}>
                  {highlight && (
                    <div className="absolute top-3 right-3 text-[9px] uppercase tracking-widest bg-[#D4AF37] text-black px-2 py-0.5 rounded">Beliebt</div>
                  )}
                  <div className="text-[10px] uppercase tracking-widest text-[#D4AF37]">{p.label}</div>
                  <div className="font-heading text-4xl mt-1">{p.price_monthly.toFixed(0)} €<span className="text-sm text-[#A8A398]"> / Mon</span></div>
                  <div className="text-xs text-[#A8A398] italic mt-1">{p.tagline}</div>
                  <ul className="mt-5 space-y-2 flex-1">
                    {(p.features || []).map((f, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm">
                        <span className="mt-1 inline-block w-1.5 h-1.5 rounded-full bg-[#D4AF37] shrink-0" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Link to="/mitglied/login" data-testid={`landing-abo-cta-${p.tier}`}
                    className="mt-6 w-full inline-flex items-center justify-center gap-2 py-3 rounded-full text-[11px] uppercase tracking-[0.4em] bg-[#D4AF37] text-black hover:bg-[#EEDC82] transitions-safe">
                    Zum Club-Login <ArrowRight size={12} />
                  </Link>
                </div>
              );
            })}
          </div>
          <div className="mt-6 text-center text-[10px] uppercase tracking-widest text-[#575148]">
            Buchung im Club-Bereich · Zahlung via Stripe · Jederzeit kündbar
          </div>
        </div>
      </section>

      {/* Film-Trivia Frage des Tages */}
      <TriviaWidget />

      {/* Event-Buchungen: Kindergeburtstag / Firmenevent / Privatvermietung */}
      <section id="events" className="relative py-20 px-6 md:px-14 border-t border-[#1A1A1A] bg-gradient-to-b from-[#120E08] to-[#0A0806]" data-testid="landing-events">
        <div className="max-w-3xl mx-auto">
          <SectionHead eyebrow="Buchen · Feiern · Mieten" title="Dein Event bei uns" icon={<Gift className="text-[#D4AF37]" size={22} />} />
          <EventBookingBlock />
        </div>
      </section>

      {/* FAQ / Hausregeln (I) */}
      {faq.length > 0 && (
        <section id="faq" className="relative py-20 px-6 md:px-14 border-t border-[#1A1A1A] bg-gradient-to-b from-[#0A0806] to-[#120E08]">
          <div className="max-w-3xl mx-auto">
            <SectionHead eyebrow="Fragen & Antworten" title="Gut zu wissen" icon={<HelpCircle className="text-[#D4AF37]" size={22} />} />
            <div className="mt-10 space-y-3" data-testid="landing-faq">
              {faq.map((item, i) => <FaqItem key={i} question={item.q} answer={item.a} />)}
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="border-t border-[#1A1A1A] py-10 px-6 md:px-14 text-[11px] uppercase tracking-[0.35em] text-[#575148]">
        <div className="max-w-[1600px] mx-auto grid md:grid-cols-3 gap-6">
          <div>
            <div className="text-[#D4AF37]">{data?.cinema_name || "THE MOVIE HOUSE"}</div>
            <div className="mt-2 text-[#F5F0E6]/60">{data?.slogan}</div>
          </div>
          <div>
            <div className="text-[#A8A398] mb-2">Öffnungszeiten</div>
            <div>{data?.opening_hours || "Nach Voranmeldung"}</div>
          </div>
          <div>
            <div className="text-[#A8A398] mb-2">Kontakt</div>
            {data?.phone && <div>{data.phone}</div>}
            {data?.address && <div>{data.address}</div>}
          </div>
        </div>
      </footer>

      <style>{`
        @keyframes fadeUp { from { opacity: 0; transform: translateY(20px) } to { opacity: 1; transform: none } }
        .animate-fade-up { animation: fadeUp 900ms cubic-bezier(.2,.7,.2,1) both; }
      `}</style>

      {/* Trailer-Lightbox (A) */}
      {trailer && <TrailerLightbox trailer={trailer} onClose={() => setTrailer(null)} />}

      {/* Gutschein-Bestell-Dialog (D) */}
      {voucherDlg && <VoucherOrderDialog amount={voucherDlg.amount} onClose={() => setVoucherDlg(null)} />}

      {/* 10er-Kinokarte-Dialog */}
      {packDlg && <TicketPackDialog pack={packDlg} onClose={() => setPackDlg(null)} />}

      {/* Film-Detail-Dialog (Klick auf Film-Karte) */}
      {movieDetail && <MovieDetailDialog movie={movieDetail} upcoming={data?.upcoming || []} onTrailer={setTrailer} onClose={() => setMovieDetail(null)} />}
      {seriesDetail && (
        <FilmreiheModal
          seriesId={seriesDetail}
          onClose={() => setSeriesDetail(null)}
          onBookMarathon={(series) => setMarathonOrderFor(series)}
        />
      )}
      {marathonOrderFor && (
        <MarathonOrderModal series={marathonOrderFor} onClose={() => setMarathonOrderFor(null)} />
      )}
    </div>
  );
}

function TrailerOfWeek({ movies }) {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (movies.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % movies.length), 30000);
    return () => clearInterval(t);
  }, [movies.length]);
  const m = movies[idx] || movies[0];
  const origin = typeof window !== "undefined" ? encodeURIComponent(window.location.origin) : "";
  const src = `https://www.youtube-nocookie.com/embed/${m.trailer_id}?autoplay=1&mute=1&controls=0&modestbranding=1&rel=0&fs=0&iv_load_policy=3&playsinline=1&cc_load_policy=0&hl=de&showinfo=0&annotations=0&loop=1&playlist=${m.trailer_id}&origin=${origin}`;
  return (
    <section className="relative border-t border-[#1A1A1A] bg-black">
      <div className="max-w-[1600px] mx-auto px-6 md:px-14 py-16">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
          <SectionHead eyebrow="Trailer der Woche" title="Vorfreude beginnt hier" icon={<Video className="text-[#D4AF37]" size={22} />} />
          {movies.length > 1 && (
            <div className="flex gap-1.5">
              {movies.map((_, i) => (
                <button key={i} onClick={() => setIdx(i)} aria-label={`Trailer ${i + 1}`}
                  data-testid={`trailer-week-dot-${i}`}
                  className={`h-[3px] rounded-full transition-all ${i === idx ? "w-10 bg-[#D4AF37]" : "w-4 bg-[#F5F0E6]/25"}`} />
              ))}
            </div>
          )}
        </div>
        <div className="relative rounded-lg overflow-hidden border border-[#1A1A1A] shadow-2xl shadow-black/60" data-testid="landing-trailer-of-week">
          <div className="relative aspect-video bg-black">
            <iframe key={m.id} src={src} title={m.title_de || m.title_original} allow="autoplay; encrypted-media"
              className="absolute inset-0 w-full h-full" />
            {/* Sehr dezente Ecken-Overlays gegen YouTube-Chrome (nur auf Desktop sichtbar) */}
            <div className="hidden md:block absolute bottom-0 left-0 w-32 h-12 bg-black/90 pointer-events-none" />
            <div className="hidden md:block absolute top-0 right-0 w-32 h-12 bg-black/90 pointer-events-none" />
            {/* Info-Overlay unten */}
            <div className="absolute inset-x-0 bottom-0 p-6 md:p-8 bg-gradient-to-t from-black via-black/80 to-transparent pointer-events-none">
              <div className="text-[10px] uppercase tracking-[0.4em] text-[#D4AF37]">
                {m.status === "demnaechst" ? "Demnächst" : "Jetzt im Kino"}
              </div>
              <div className="font-heading text-3xl md:text-4xl text-[#F5F0E6] mt-1 max-w-3xl">
                {m.title_de || m.title_original}
              </div>
              {(m.genre || m.duration_min || m.age_rating || m.rating) && (
                <div className="mt-2 text-[10px] uppercase tracking-[0.32em] text-[#A8A398]">
                  {[
                    m.genre,
                    m.duration_min ? `${m.duration_min} Min` : "",
                    (m.age_rating || m.rating) ? String(m.age_rating || m.rating).replace(/^FSK\s*/i, "FSK ") : "",
                  ].filter(Boolean).join(" · ")}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function VoucherOrderDialog({ amount, onClose }) {
  const [recipient, setRecipient] = useState("");
  const [fromName, setFromName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [theme, setTheme] = useState("classic");
  const [backdropUrl, setBackdropUrl] = useState("");
  const [movieQuery, setMovieQuery] = useState("");
  const [movieResults, setMovieResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Live-Movie-Suche für theme=movie
  useEffect(() => {
    if (theme !== "movie" || !movieQuery.trim() || movieQuery.length < 2) { setMovieResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/tmdb/search?q=${encodeURIComponent(movieQuery)}`);
        setMovieResults((r.data?.results || []).slice(0, 5));
      } catch { setMovieResults([]); }
    }, 400);
    return () => clearTimeout(t);
  }, [movieQuery, theme]);

  const submit = async (e) => {
    e.preventDefault();
    if (!recipient.trim() || !email.trim()) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/vouchers/order`, {
        amount,
        recipient_name: recipient,
        email,
        from_name: fromName,
        message,
        theme,
        favorite_backdrop_url: backdropUrl,
      });
      toast.success(`Gutschein · Code ${data.code}`);
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bestellung fehlgeschlagen");
    } finally { setBusy(false); }
  };

  const THEMES = [
    { v: "classic", l: "Klassisch", desc: "Schwarz + Gold" },
    { v: "christmas", l: "Weihnachten", desc: "Rot + Grün · Schneeflocken" },
    { v: "movie", l: "Lieblingsfilm", desc: "Backdrop im Hintergrund" },
  ];

  return (
    <div className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose} data-testid="voucher-order-dialog">
      <div className="relative w-full max-w-lg bg-[#0F0D08] border border-[#D4AF37]/40 rounded-lg p-8 shadow-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 text-[#F5F0E6]/60 hover:text-[#D4AF37]" aria-label="Schließen">
          <X size={22} />
        </button>
        <div className="text-[10px] uppercase tracking-[0.4em] text-[#D4AF37] flex items-center gap-2">
          <Gift size={14} /> Gutschein bestellen
        </div>
        <div className="font-heading text-4xl text-[#D4AF37] mt-2">{amount} €</div>
        {done ? (
          <div className="mt-6 space-y-4 text-center">
            <div className="w-14 h-14 rounded-full bg-[#D4AF37]/20 mx-auto flex items-center justify-center">
              <Mail className="text-[#D4AF37]" size={26} />
            </div>
            <div className="font-heading text-2xl">Vielen Dank!</div>
            <p className="text-sm text-[#F5F0E6]/80">
              Der Gutschein kommt gleich als PDF an <span className="text-[#D4AF37]">{email}</span>.
            </p>
            <button onClick={onClose} data-testid="voucher-close-ok"
              className="mt-4 px-6 py-3 bg-[#D4AF37] text-black rounded-full text-[11px] uppercase tracking-[0.4em] hover:bg-[#EEDC82]">
              Schließen
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-6 space-y-4">
            {/* Theme-Auswahl */}
            <div>
              <label className="text-[10px] uppercase tracking-[0.32em] text-[#A8A398]">Design</label>
              <div className="mt-2 grid grid-cols-3 gap-2">
                {THEMES.map((t) => (
                  <button key={t.v} type="button" onClick={() => setTheme(t.v)} data-testid={`voucher-theme-${t.v}`}
                    className={`px-2 py-2 rounded border text-left transition-colors ${theme === t.v ? "border-[#D4AF37] bg-[#D4AF37]/[0.08]" : "border-[#2A2A2A] hover:border-[#575148]"}`}>
                    <div className="text-[11px] font-heading text-[#F5F0E6]">{t.l}</div>
                    <div className="text-[9px] uppercase tracking-[0.2em] text-[#A8A398] mt-0.5">{t.desc}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Film-Suche wenn theme=movie */}
            {theme === "movie" && (
              <div>
                <label className="text-[10px] uppercase tracking-[0.32em] text-[#A8A398]">Lieblingsfilm des Empfängers</label>
                <input value={movieQuery} onChange={(e) => setMovieQuery(e.target.value)} data-testid="voucher-movie-search"
                  placeholder="Film-Titel suchen …"
                  className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 outline-none focus:border-[#D4AF37]" />
                {movieResults.length > 0 && !backdropUrl && (
                  <div className="mt-2 space-y-1">
                    {movieResults.map((m) => (
                      <button key={m.tmdb_id || m.id} type="button" onClick={() => { setBackdropUrl(m.backdrop_url || m.poster_url); setMovieQuery(m.title_de || m.title_original); setMovieResults([]); }}
                        data-testid={`voucher-movie-${m.tmdb_id || m.id}`}
                        className="w-full flex items-center gap-3 p-2 border border-[#2A2A2A] rounded hover:border-[#D4AF37] text-left">
                        {m.poster_url && <img src={m.poster_url} alt="" className="w-8 h-12 object-cover rounded" />}
                        <div className="flex-1 min-w-0">
                          <div className="text-sm truncate">{m.title_de || m.title_original}</div>
                          <div className="text-[9px] uppercase tracking-[0.3em] text-[#A8A398]">{m.year || ""}{m.backdrop_url ? " · Backdrop ✓" : ""}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
                {backdropUrl && (
                  <div className="mt-2 flex items-center gap-2 p-2 border border-[#D4AF37]/40 rounded bg-[#D4AF37]/[0.05]">
                    <img src={backdropUrl} alt="" className="w-16 h-9 object-cover rounded" />
                    <div className="flex-1 text-xs text-[#F5F0E6]">{movieQuery}</div>
                    <button type="button" onClick={() => { setBackdropUrl(""); setMovieQuery(""); }} className="text-xs text-[#EE9CA1]">Entfernen</button>
                  </div>
                )}
              </div>
            )}

            <Field label="Empfänger" required value={recipient} onChange={setRecipient} testid="voucher-recipient" placeholder="Für wen ist der Gutschein?" />
            <Field label="Deine E-Mail" required type="email" value={email} onChange={setEmail} testid="voucher-email" placeholder="Wir schicken das PDF hierhin" />
            <Field label="Von (optional)" value={fromName} onChange={setFromName} testid="voucher-from" placeholder="Dein Name" />
            <div>
              <label className="text-[10px] uppercase tracking-[0.32em] text-[#A8A398]">Persönliche Nachricht (optional)</label>
              <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={3} data-testid="voucher-message"
                placeholder="Für einen schönen Kinoabend …"
                className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 outline-none focus:border-[#D4AF37] text-[#F5F0E6]" />
            </div>
            <button type="submit" disabled={busy} data-testid="voucher-submit"
              className="w-full bg-[#D4AF37] text-black rounded px-6 py-3 text-[11px] uppercase tracking-[0.4em] hover:bg-[#EEDC82] disabled:opacity-40 flex items-center justify-center gap-2">
              {busy ? <><Loader2 size={14} className="animate-spin" /> Wird verschickt …</> : <><Gift size={14} /> Für {amount} € bestellen</>}
            </button>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#575148] text-center">
              Kein Konto nötig · PDF direkt per Mail · QR-Code an der Kasse einlösbar
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function TicketPackDialog({ pack, onClose }) {
  const [recipient, setRecipient] = useState("");
  const [fromName, setFromName] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  const submit = async (e) => {
    e.preventDefault();
    if (!recipient.trim() || !email.trim()) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/ticket-packs/order`, {
        ticket_count: pack.count,
        price_per_ticket: pack.price,
        recipient_name: recipient,
        email,
        from_name: fromName,
        theme: "classic",
      });
      toast.success(`${pack.count}er-Karte · Code ${data.code}`);
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bestellung fehlgeschlagen");
    } finally { setBusy(false); }
  };
  const total = pack.count * pack.price;
  return (
    <div className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose} data-testid="ticket-pack-dialog">
      <div className="relative w-full max-w-lg bg-[#0F0D08] border border-[#D4AF37]/40 rounded-lg p-8 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 text-[#F5F0E6]/60 hover:text-[#D4AF37]"><X size={22} /></button>
        <div className="text-[10px] uppercase tracking-[0.4em] text-[#D4AF37] flex items-center gap-2">
          <Ticket size={14} /> {pack.count}er-Kinokarte
        </div>
        <div className="font-heading text-4xl text-[#D4AF37] mt-2">{total.toFixed(2)} €</div>
        <div className="text-xs text-[#A8A398] mt-1">{pack.count} Eintritte · {pack.price.toFixed(2)} € pro Ticket</div>
        {done ? (
          <div className="mt-6 text-center space-y-4">
            <div className="w-14 h-14 rounded-full bg-[#D4AF37]/20 mx-auto flex items-center justify-center">
              <Mail className="text-[#D4AF37]" size={26} />
            </div>
            <div className="font-heading text-2xl">Vielen Dank!</div>
            <p className="text-sm text-[#F5F0E6]/80">Deine Kinokarte kommt sofort als PDF an <span className="text-[#D4AF37]">{email}</span>.</p>
            <button onClick={onClose} className="mt-4 px-6 py-3 bg-[#D4AF37] text-black rounded-full text-[11px] uppercase tracking-[0.4em] hover:bg-[#EEDC82]">Schließen</button>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-6 space-y-4">
            <Field label="Empfänger" required value={recipient} onChange={setRecipient} testid="pack-recipient" placeholder="Für wen ist die Karte?" />
            <Field label="Deine E-Mail" required type="email" value={email} onChange={setEmail} testid="pack-email" placeholder="Wir schicken das PDF hierhin" />
            <Field label="Von (optional)" value={fromName} onChange={setFromName} testid="pack-from" />
            <button type="submit" disabled={busy} data-testid="pack-submit"
              className="w-full bg-[#D4AF37] text-black rounded px-6 py-3 text-[11px] uppercase tracking-[0.4em] hover:bg-[#EEDC82] disabled:opacity-40 flex items-center justify-center gap-2">
              {busy ? <><Loader2 size={14} className="animate-spin" /> Wird verschickt …</> : <><Ticket size={14} /> Für {total.toFixed(2)} € bestellen</>}
            </button>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#575148] text-center">
              An der Kinokasse abstempeln · 2 Jahre gültig
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, placeholder, testid }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.32em] text-[#A8A398]">
        {label}{required && <span className="text-[#D4AF37] ml-1">*</span>}
      </label>
      <input type={type} required={required} value={value} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 outline-none focus:border-[#D4AF37] text-[#F5F0E6]" />
    </div>
  );
}

function TrailerLightbox({ trailer, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  const origin = typeof window !== "undefined" ? encodeURIComponent(window.location.origin) : "";
  const src = `https://www.youtube-nocookie.com/embed/${trailer.ytId}?autoplay=1&controls=1&modestbranding=1&rel=0&iv_load_policy=3&playsinline=1&cc_load_policy=0&hl=de&showinfo=0&annotations=0&origin=${origin}`;
  return (
    <div className="fixed inset-0 z-50 bg-black/95 backdrop-blur-md flex items-center justify-center p-4"
      onClick={onClose} data-testid="landing-trailer-modal">
      <button onClick={onClose} className="absolute top-6 right-6 text-[#F5F0E6]/70 hover:text-[#D4AF37] z-10" aria-label="Schließen">
        <X size={28} />
      </button>
      <div className="text-[10px] uppercase tracking-[0.4em] text-[#D4AF37] absolute top-6 left-6">{trailer.title}</div>
      <div className="w-full max-w-6xl aspect-video relative" onClick={(e) => e.stopPropagation()}>
        <iframe src={src} title="Trailer" allow="autoplay; encrypted-media"
          className="w-full h-full rounded-lg shadow-2xl shadow-black/60 border border-[#D4AF37]/20" />
      {/* Sehr dezente Ecken-Overlays gegen Kinocheck-Branding (nur Desktop) */}
        <div className="hidden md:block absolute bottom-0 left-0 w-40 h-12 bg-black/90 pointer-events-none rounded-bl-lg" />
        <div className="hidden md:block absolute top-0 right-0 w-40 h-12 bg-black/90 pointer-events-none rounded-tr-lg" />
      </div>
    </div>
  );
}

function NextShowCountdown({ upcoming }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const next = useMemo(() => {
    for (const st of upcoming) {
      const dt = new Date(`${st.date}T${st.start_time}:00`);
      if (dt.getTime() > now) return { st, dt };
    }
    return null;
  }, [upcoming, now]);
  if (!next) return null;
  const diff = Math.max(0, next.dt.getTime() - now);
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  return (
    <div className="mt-8 inline-flex items-center gap-4 px-5 py-3 border border-[#D4AF37]/30 rounded-full bg-black/40 backdrop-blur animate-fade-up"
      data-testid="landing-countdown" style={{ animationDelay: "340ms" }}>
      <div className="w-2 h-2 rounded-full bg-[#D4AF37] animate-pulse" />
      <div className="text-[10px] uppercase tracking-[0.4em] text-[#D4AF37]">Nächste Vorstellung</div>
      <div className="font-mono text-[#F5F0E6] text-sm tabular-nums">
        {h > 0 && `${h}h `}{m.toString().padStart(2, "0")}m {s.toString().padStart(2, "0")}s
      </div>
    </div>
  );
}

function PromoBanner({ promo }) {
  return (
    <section className="relative border-y border-[#D4AF37]/30 py-6 px-6 md:px-14 bg-[#D4AF37]/[0.07]" data-testid="landing-promo">
      <div className="max-w-[1600px] mx-auto flex items-center gap-4 flex-wrap justify-center md:justify-between">
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
            <PartyPopper size={18} className="text-[#D4AF37]" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.4em] text-[#D4AF37]">{promo.title}</div>
            <div className="text-sm text-[#F5F0E6] mt-0.5">{promo.text}</div>
          </div>
        </div>
        {promo.cta && promo.cta_href && (
          <a href={promo.cta_href}
            className="text-[10px] uppercase tracking-[0.4em] px-4 py-2 border border-[#D4AF37] text-[#D4AF37] rounded-full hover:bg-[#D4AF37] hover:text-black transition-colors whitespace-nowrap">
            {promo.cta} <ArrowRight size={11} className="inline ml-1" />
          </a>
        )}
      </div>
    </section>
  );
}

function TestimonialCarousel({ items, idx, setIdx }) {
  useEffect(() => {
    if (items.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % items.length), 6500);
    return () => clearInterval(t);
  }, [items.length, setIdx]);
  const t = items[idx] || items[0];
  return (
    <div className="mt-10">
      <div className="relative min-h-[140px] flex items-center justify-center" data-testid="landing-testimonials">
        <div key={idx} className="animate-fade-up">
          <p className="font-heading text-2xl md:text-3xl italic text-[#F5F0E6]/90 max-w-2xl leading-relaxed">
            „{t?.text}"
          </p>
          <div className="mt-4 text-[10px] uppercase tracking-[0.5em] text-[#D4AF37]">— {t?.author}</div>
        </div>
      </div>
      {items.length > 1 && (
        <div className="mt-6 flex gap-2 justify-center">
          {items.map((_, i) => (
            <button key={i} onClick={() => setIdx(i)} aria-label={`Zitat ${i + 1}`}
              className={`h-[3px] rounded-full transition-all ${i === idx ? "w-8 bg-[#D4AF37]" : "w-3 bg-[#F5F0E6]/30"}`} />
          ))}
        </div>
      )}
    </div>
  );
}

function FaqItem({ question, answer }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`border rounded-lg transition-colors ${open ? "border-[#D4AF37]/60 bg-[#D4AF37]/[0.04]" : "border-[#1A1A1A] hover:border-[#2A2A2A]"}`}>
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between gap-4 p-4 text-left" data-testid="faq-item">
        <span className="text-sm md:text-base text-[#F5F0E6]">{question}</span>
        <ChevronDown size={18} className={`text-[#D4AF37] transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="px-4 pb-4 text-sm text-[#A8A398] leading-relaxed">{answer}</div>}
    </div>
  );
}

function SectionHead({ eyebrow, title, center, icon }) {
  return (
    <div className={center ? "text-center" : ""}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.5em] text-[#D4AF37]"
        style={{ justifyContent: center ? "center" : "flex-start" }}>
        {icon}<span>{eyebrow}</span>
      </div>
      <h2 className="font-heading text-4xl md:text-5xl mt-2">{title}</h2>
    </div>
  );
}

function DayBlock({ isoDate, shows }) {
  const d = new Date(isoDate + "T00:00:00");
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const diff = Math.round((d - today) / 86400000);
  const label = diff === 0 ? "Heute" : diff === 1 ? "Morgen" : d.toLocaleDateString("de-DE", { weekday: "long" });
  const dayLabel = d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" });

  const byMovie = new Map();
  for (const s of shows) {
    const k = s.movie?.id || s.movie_id;
    if (!byMovie.has(k)) byMovie.set(k, { movie: s.movie, shows: [], event_type: s.event_type });
    byMovie.get(k).shows.push(s);
  }

  return (
    <div>
      <div className="flex items-baseline gap-4 border-b border-[#D4AF37]/30 pb-3 mb-6">
        <div className="font-heading text-3xl text-[#D4AF37]">{label}</div>
        <div className="text-[11px] uppercase tracking-[0.4em] text-[#575148]">{dayLabel}</div>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {Array.from(byMovie.values()).map(({ movie, shows: mshows, event_type }, gi) => {
          const badge = EVENT_STYLES[event_type];
          return (
            <div key={gi} className="flex gap-4 border-l-2 border-[#D4AF37]/25 pl-4">
              {movie?.poster_url && (
                <div className="relative shrink-0">
                  <img src={movie.poster_url} alt="" className="w-20 h-30 object-cover rounded"
                    onError={(e) => { e.currentTarget.style.display = "none"; }} />
                  {badge && (
                    <div className={`absolute -top-1 -left-1 text-[8px] uppercase tracking-[0.3em] px-1.5 py-0.5 rounded ${badge.cls}`}>
                      {badge.label}
                    </div>
                  )}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="font-heading text-lg leading-tight">{movie?.title_de || movie?.title_original || "—"}</div>
                <div className="text-[10px] uppercase tracking-[0.32em] text-[#A8A398] mt-1">
                  {movie?.genre || ""} · FSK {(movie?.age_rating || movie?.rating || "?").toString().replace(/^FSK\s*/i, "")}
                </div>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {mshows.map((s) => (
                    <Link key={s.id} to={`/kiosk/ticket/${s.id}`} data-testid={`landing-showtime-${s.id}`}
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded border border-[#D4AF37]/40 bg-black/40 hover:bg-[#D4AF37] hover:text-black transition-colors text-xs font-mono">
                      <Clock size={10} /> {s.start_time}
                      <span className="text-[9px] uppercase tracking-[0.3em] opacity-70 ml-1">{s.hall || ""}</span>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function MovieCard({ movie, soon, onTrailer, onOpen }) {
  const badge = EVENT_STYLES[movie.event_type];
  const age = movie.age_rating || movie.rating;
  const isNew = movie.status === "neu";
  return (
    <div className="group relative rounded-lg overflow-hidden bg-[#111] border border-[#1A1A1A] hover:border-[#D4AF37]/50 transition-colors cursor-pointer"
      onClick={() => onOpen && onOpen(movie)} data-testid={`movie-card-${movie.id}`}>
      {movie.poster_url ? (
        <img src={movie.poster_url} alt="" className="w-full aspect-[2/3] object-cover group-hover:scale-[1.03] transition-transform duration-700"
          onError={(e) => { e.currentTarget.style.display = "none"; }} />
      ) : (
        <div className="w-full aspect-[2/3] flex items-center justify-center bg-[#050505]"><Film size={32} className="text-[#575148]" /></div>
      )}
      {isNew && (
        <div className="absolute top-2 left-2 text-[8px] uppercase tracking-[0.4em] px-2 py-0.5 rounded-full bg-[#7A1921] text-white">Neu</div>
      )}
      {soon && !isNew && (
        <div className="absolute top-2 left-2 text-[8px] uppercase tracking-[0.4em] px-2 py-0.5 rounded-full bg-[#D4AF37] text-black">Bald</div>
      )}
      {badge && (
        <div className={`absolute top-2 right-2 text-[8px] uppercase tracking-[0.3em] px-2 py-0.5 rounded-full ${badge.cls}`}>
          {badge.label}
        </div>
      )}
      {age && (
        <div className={`absolute ${badge ? "top-9" : "top-2"} right-2 text-[9px] font-heading font-bold px-2 py-0.5 rounded-full bg-black/80 border border-[#D4AF37]/50 text-[#D4AF37]`}>
          {String(age).replace(/^FSK\s*/i, "FSK ").trim() || `FSK ${age}`}
        </div>
      )}
      {movie.trailer_id && onTrailer && (
        <button
          onClick={(e) => { e.stopPropagation(); onTrailer({ ytId: movie.trailer_id, title: movie.title_de || movie.title_original }); }}
          data-testid={`movie-trailer-${movie.id}`}
          className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"
          aria-label="Trailer ansehen">
          <div className="w-14 h-14 rounded-full bg-[#D4AF37] text-black flex items-center justify-center pointer-events-auto shadow-lg hover:scale-110 transition-transform">
            <Play size={22} />
          </div>
        </button>
      )}
      <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black via-black/70 to-transparent pointer-events-none">
        <div className="font-heading text-sm leading-tight truncate">{movie.title_de || movie.title_original}</div>
        {(movie.genre || movie.duration_min) && (
          <div className="text-[9px] uppercase tracking-[0.32em] text-[#A8A398] mt-0.5 truncate">
            {[movie.genre, movie.duration_min ? `${movie.duration_min} Min` : ""].filter(Boolean).join(" · ")}
          </div>
        )}
      </div>
    </div>
  );
}

function MovieDetailDialog({ movie, upcoming, onClose, onTrailer }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const k = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [onClose]);
  useEffect(() => {
    let cancel = false;
    setLoading(true);
    axios.get(`${API}/public/movie/${movie.id}`, {
      headers: localStorage.getItem("mh_landing_pin") ? { "X-Landing-Pin": localStorage.getItem("mh_landing_pin") } : {},
    })
      .then((r) => { if (!cancel) setDetail(r.data); })
      .catch(() => { if (!cancel) setDetail(null); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [movie.id]);

  const shows = (detail?.shows && detail.shows.length > 0)
    ? detail.shows
    : (upcoming || []).filter((s) => s.movie_id === movie.id || s.movie?.id === movie.id);
  const enriched = { ...movie, ...(detail?.movie || {}) };
  const tmdbX = detail?.tmdb || {};
  const reviews = detail?.reviews || { average: null, count: 0, recent: [] };
  const similar = detail?.similar || [];
  const playbackHint = detail?.playback_hint;

  const age = enriched.age_rating || enriched.rating;
  const showsByDate = useMemo(() => {
    const m = new Map();
    for (const s of shows) {
      if (!m.has(s.date)) m.set(s.date, []);
      m.get(s.date).push(s);
    }
    return Array.from(m.entries());
  }, [shows]);

  return (
    <div className="fixed inset-0 z-50 bg-black/95 backdrop-blur-md flex items-center justify-center p-4"
      onClick={onClose} data-testid="movie-detail-dialog">
      <div className="relative w-full max-w-4xl max-h-[92vh] overflow-y-auto bg-[#0F0D08] border border-[#D4AF37]/40 rounded-lg" onClick={(e) => e.stopPropagation()}>
        {/* Backdrop */}
        {enriched.backdrop_url && (
          <div className="relative h-64 md:h-96 overflow-hidden rounded-t-lg">
            <img src={enriched.backdrop_url} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0F0D08] via-[#0F0D08]/50 to-transparent" />
          </div>
        )}
        <button onClick={onClose} className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-black/70 hover:bg-[#D4AF37] hover:text-black text-[#F5F0E6] flex items-center justify-center" aria-label="Schließen">
          <X size={20} />
        </button>

        <div className="p-6 md:p-10 -mt-24 relative z-10">
          <div className="flex items-start gap-6 flex-wrap">
            {enriched.poster_url && (
              <img src={enriched.poster_url} alt="" className="w-40 md:w-52 aspect-[2/3] object-cover rounded-lg shadow-2xl shrink-0" />
            )}
            <div className="flex-1 min-w-[240px]">
              <div className="text-[10px] uppercase tracking-[0.5em] text-[#D4AF37]">
                {enriched.status === "demnaechst" ? "Demnächst im Kino" : "Jetzt im Programm"}
              </div>
              <h2 className="font-heading text-4xl md:text-5xl mt-1">{enriched.title_de || enriched.title_original}</h2>
              {enriched.title_original && enriched.title_original !== enriched.title_de && (
                <div className="text-sm text-[#A8A398] italic mt-1">{enriched.title_original}</div>
              )}
              {tmdbX.tagline && (
                <div className="mt-3 text-[#EEDC82] italic text-sm md:text-base" data-testid="movie-detail-tagline">
                  „{tmdbX.tagline}"
                </div>
              )}
              <div className="mt-4 flex flex-wrap gap-2 text-[10px] uppercase tracking-[0.35em]">
                {enriched.year && <Chip>{enriched.year}</Chip>}
                {enriched.genre && <Chip>{enriched.genre}</Chip>}
                {enriched.duration_min ? <Chip>{enriched.duration_min} Min</Chip> : null}
                {age && <Chip gold>{String(age).match(/^FSK/i) ? age : `FSK ${age}`}</Chip>}
                {enriched.language && <Chip>{enriched.language}</Chip>}
                {tmdbX.original_language && tmdbX.original_language !== "de" && (
                  <Chip>Original {String(tmdbX.original_language).toUpperCase()}</Chip>
                )}
                {playbackHint?.source && (
                  <Chip>Quelle: {playbackHint.source}</Chip>
                )}
              </div>

              {/* Rating-Zeile: eigene Reviews + TMDB */}
              <div className="mt-4 flex flex-wrap items-center gap-6" data-testid="movie-detail-ratings">
                {reviews.average != null && reviews.count > 0 && (
                  <RatingPill label="Publikum" value={reviews.average} scale={5} count={reviews.count}
                              testid="movie-detail-audience-rating" />
                )}
                {tmdbX.vote_average > 0 && (
                  <RatingPill label="TMDb" value={tmdbX.vote_average} scale={10} count={tmdbX.vote_count}
                              tone="#EEDC82" testid="movie-detail-tmdb-rating" />
                )}
                {enriched.personal_rating > 0 && (
                  <RatingPill label="Kritiker" value={enriched.personal_rating} scale={5}
                              tone="#EE9CA1" testid="movie-detail-personal-rating" />
                )}
              </div>

              {(enriched.trailer_id || shows.length > 0) && (
                <div className="mt-6 flex flex-wrap items-center gap-3">
                  {shows.length > 0 && (
                    <Link to={`/kiosk/ticket/${shows[0].id}`}
                      data-testid="movie-detail-book"
                      className="inline-flex items-center gap-2 px-6 py-3 bg-[#D4AF37] text-black rounded-full text-xs uppercase tracking-[0.4em] hover:bg-[#EEDC82]">
                      <Ticket size={14} /> Ticket sichern
                    </Link>
                  )}
                  {enriched.trailer_id && (
                    <button onClick={() => onTrailer({ ytId: enriched.trailer_id, title: enriched.title_de })}
                      data-testid="movie-detail-trailer"
                      className={`inline-flex items-center gap-2 px-6 py-3 rounded-full text-xs uppercase tracking-[0.4em] transition-colors ${shows.length > 0 ? "border border-[#F5F0E6]/30 text-[#F5F0E6] hover:border-[#D4AF37] hover:text-[#D4AF37]" : "bg-[#D4AF37] text-black hover:bg-[#EEDC82]"}`}>
                      <Play size={14} /> Trailer ansehen
                    </button>
                  )}
                </div>
              )}
              {shows.length === 0 && enriched.status !== "demnaechst" && (
                <div className="mt-4 text-[11px] text-[#A8A398] italic" data-testid="movie-detail-noshows">
                  Aktuell keine Vorstellung geplant · demnächst im Programm.
                </div>
              )}
            </div>
          </div>

          {(enriched.synopsis || enriched.description) && (
            <div className="mt-8">
              <div className="text-[10px] uppercase tracking-[0.4em] text-[#A8A398]">Handlung</div>
              <p className="mt-2 text-[#F5F0E6] leading-relaxed">{enriched.synopsis || enriched.description}</p>
            </div>
          )}

          {/* Besetzung mit Fotos (TMDB) */}
          {tmdbX.cast_list && tmdbX.cast_list.length > 0 && (
            <div className="mt-8" data-testid="movie-detail-cast">
              <div className="text-[10px] uppercase tracking-[0.4em] text-[#A8A398] mb-3">Besetzung</div>
              <div className="flex gap-3 overflow-x-auto pb-2 snap-x">
                {tmdbX.cast_list.slice(0, 10).map((c, idx) => (
                  <div key={idx} className="shrink-0 w-24 text-center snap-start">
                    {c.profile_url ? (
                      <img src={c.profile_url} alt={c.name}
                        className="w-24 h-32 object-cover rounded border border-[#2A2A2A]" />
                    ) : (
                      <div className="w-24 h-32 rounded border border-[#2A2A2A] bg-[#141414] flex items-center justify-center text-[#575148]">
                        <User size={22} />
                      </div>
                    )}
                    <div className="text-xs mt-1.5 leading-tight text-[#F5F0E6] truncate" title={c.name}>{c.name}</div>
                    {c.character && <div className="text-[9px] uppercase tracking-[0.2em] text-[#575148] truncate" title={c.character}>{c.character}</div>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Regie / Cast (Text-Fallback wenn keine TMDB-Cast-Fotos) */}
          {(!tmdbX.cast_list || tmdbX.cast_list.length === 0) && (enriched.director || enriched.cast) && (
            <div className="mt-6 grid md:grid-cols-2 gap-4 text-sm">
              {enriched.director && <Info label="Regie" value={enriched.director} />}
              {enriched.cast && <Info label="Besetzung" value={enriched.cast} />}
            </div>
          )}
          {tmdbX.cast_list && tmdbX.cast_list.length > 0 && enriched.director && (
            <div className="mt-4 text-sm">
              <Info label="Regie" value={enriched.director} />
            </div>
          )}

          {/* Impressionen — Backdrops-Galerie */}
          {tmdbX.backdrops && tmdbX.backdrops.length > 1 && (
            <div className="mt-8" data-testid="movie-detail-backdrops">
              <div className="text-[10px] uppercase tracking-[0.4em] text-[#A8A398] mb-3">Impressionen</div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {tmdbX.backdrops.slice(1, 7).map((url, idx) => (
                  <a key={idx} href={url} target="_blank" rel="noreferrer"
                    className="block relative aspect-video overflow-hidden rounded border border-[#1A1A1A] hover:border-[#D4AF37]/60">
                    <img src={url} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform" />
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Vorstellungen · nach Datum */}
          {shows.length > 0 && (
            <div className="mt-8" data-testid="movie-detail-shows">
              <div className="text-[10px] uppercase tracking-[0.4em] text-[#A8A398] mb-3">Vorstellungen · direkt buchbar</div>
              <div className="space-y-3">
                {showsByDate.slice(0, 5).map(([date, list]) => {
                  const d = new Date(date + "T00:00:00");
                  const dayLabel = d.toLocaleDateString("de-DE", { weekday: "long", day: "2-digit", month: "2-digit" });
                  return (
                    <div key={date} className="flex flex-wrap items-center gap-2">
                      <div className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] w-full md:w-40 shrink-0">{dayLabel}</div>
                      <div className="flex flex-wrap gap-2">
                        {list.map((s) => (
                          <Link key={s.id} to={`/kiosk/ticket/${s.id}`}
                            data-testid={`movie-detail-show-${s.id}`}
                            className="inline-flex items-center gap-2 px-3 py-1.5 rounded border border-[#D4AF37]/40 bg-black/40 hover:bg-[#D4AF37] hover:text-black transition-colors font-mono text-sm">
                            <Clock size={11} /> {s.start_time}
                            <span className="text-[9px] uppercase tracking-[0.3em] opacity-70">{s.hall}</span>
                          </Link>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Bewertungen der Gäste */}
          <MovieReviewsSection movieId={movie.id} summary={reviews} loading={loading} />

          {/* Ähnliche Filme aus unserem Programm */}
          {similar.length > 0 && (
            <div className="mt-10" data-testid="movie-detail-similar">
              <div className="text-[10px] uppercase tracking-[0.4em] text-[#A8A398] mb-3">Ähnliche Filme im Programm</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {similar.slice(0, 8).map((s) => (
                  <div key={s.id} className="cursor-pointer rounded overflow-hidden bg-[#111] border border-[#1A1A1A] hover:border-[#D4AF37]/50 transition-colors"
                    data-testid={`movie-detail-similar-${s.id}`}>
                    {s.poster_url ? (
                      <img src={s.poster_url} alt="" className="w-full aspect-[2/3] object-cover" />
                    ) : (
                      <div className="w-full aspect-[2/3] flex items-center justify-center bg-[#050505]"><Film size={22} className="text-[#575148]" /></div>
                    )}
                    <div className="p-2">
                      <div className="text-xs font-heading truncate">{s.title_de}</div>
                      <div className="text-[9px] uppercase tracking-[0.32em] text-[#575148] truncate">{s.genre || ""}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function RatingPill({ label, value, scale = 5, count = 0, tone = "#D4AF37", testid }) {
  const pct = Math.max(0, Math.min(1, Number(value || 0) / scale));
  const stars = 5;
  const filled = Math.round(pct * stars);
  return (
    <div className="flex items-center gap-2" data-testid={testid}>
      <div className="text-[9px] uppercase tracking-[0.32em] text-[#A8A398]">{label}</div>
      <div className="flex items-center gap-0.5">
        {Array.from({ length: stars }).map((_, i) => (
          <Star key={i} size={12}
            className={i < filled ? "" : "opacity-30"}
            fill={i < filled ? tone : "transparent"}
            color={tone} />
        ))}
      </div>
      <div className="font-mono text-sm" style={{ color: tone }}>
        {Number(value).toFixed(1)}<span className="text-[10px] text-[#575148]"> / {scale}</span>
      </div>
      {count > 0 && <div className="text-[10px] text-[#575148]">({count})</div>}
    </div>
  );
}

function MovieReviewsSection({ movieId, summary, loading }) {
  const [showForm, setShowForm] = useState(false);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [localSummary, setLocalSummary] = useState(summary);
  useEffect(() => { setLocalSummary(summary); }, [summary]);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/reviews`, {
        movie_id: movieId,
        rating: Number(rating),
        comment: comment.trim(),
        guest_name: (name || "Gast").trim(),
      });
      toast.success("Danke für deine Bewertung! Sie erscheint hier, sobald sie freigegeben wurde.");
      // Refresh
      try {
        const { data } = await axios.get(`${API}/public/movie/${movieId}`, {
          headers: localStorage.getItem("mh_landing_pin") ? { "X-Landing-Pin": localStorage.getItem("mh_landing_pin") } : {},
        });
        setLocalSummary(data.reviews);
      } catch { /* noop */ }
      setShowForm(false); setRating(5); setComment(""); setName("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bewertung konnte nicht gespeichert werden");
    } finally { setBusy(false); }
  };
  const recent = localSummary?.recent || [];
  return (
    <div className="mt-10" data-testid="movie-detail-reviews">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.4em] text-[#A8A398]">Bewertungen</div>
          {localSummary?.average != null && localSummary.count > 0 ? (
            <div className="mt-1 flex items-center gap-2">
              <div className="flex items-center gap-0.5">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} size={14}
                    className={i < Math.round(localSummary.average) ? "" : "opacity-30"}
                    fill={i < Math.round(localSummary.average) ? "#D4AF37" : "transparent"}
                    color="#D4AF37" />
                ))}
              </div>
              <span className="font-mono text-[#D4AF37]">{Number(localSummary.average).toFixed(1)}<span className="text-xs text-[#575148]"> / 5</span></span>
              <span className="text-xs text-[#A8A398]">· {localSummary.count} Bewertung{localSummary.count === 1 ? "" : "en"}</span>
            </div>
          ) : (
            <div className="mt-1 text-[11px] text-[#575148] italic">Noch keine Bewertungen — sei die/der Erste!</div>
          )}
        </div>
        <button onClick={() => setShowForm((v) => !v)} data-testid="movie-review-toggle"
          className="text-[10px] uppercase tracking-[0.4em] px-4 py-2 border border-[#D4AF37] text-[#D4AF37] rounded-full hover:bg-[#D4AF37] hover:text-black transition-colors">
          {showForm ? "Abbrechen" : "Bewerten"}
        </button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="mb-6 bg-black/40 border border-[#2A2A2A] rounded-lg p-4 space-y-3" data-testid="movie-review-form">
          <div className="flex items-center gap-2">
            <div className="text-[10px] uppercase tracking-[0.32em] text-[#A8A398] mr-3">Deine Wertung</div>
            {[1, 2, 3, 4, 5].map((n) => (
              <button type="button" key={n} onClick={() => setRating(n)}
                data-testid={`movie-review-star-${n}`}
                aria-label={`${n} Sterne`}
                className="p-1">
                <Star size={22}
                  fill={n <= rating ? "#D4AF37" : "transparent"}
                  color="#D4AF37" />
              </button>
            ))}
          </div>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Dein Name (optional)"
            data-testid="movie-review-name"
            className="w-full bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 outline-none focus:border-[#D4AF37]" />
          <textarea value={comment} onChange={(e) => setComment(e.target.value)}
            data-testid="movie-review-comment"
            placeholder="Was hat dir gefallen? (optional)" rows={3}
            className="w-full bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 outline-none focus:border-[#D4AF37] resize-none" />
          <button type="submit" disabled={busy}
            data-testid="movie-review-submit"
            className="inline-flex items-center gap-2 px-6 py-2.5 bg-[#D4AF37] text-black rounded-full text-[10px] uppercase tracking-[0.4em] hover:bg-[#EEDC82] disabled:opacity-40">
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Star size={12} />} Bewertung senden
          </button>
        </form>
      )}

      {loading && !recent.length ? (
        <div className="text-center py-6 text-[#575148]"><Loader2 size={16} className="animate-spin inline" /></div>
      ) : recent.length > 0 ? (
        <div className="space-y-3" data-testid="movie-reviews-list">
          {recent.map((r, idx) => (
            <div key={idx} className="bg-black/30 border border-[#1A1A1A] rounded-lg p-4">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-0.5">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star key={i} size={12}
                        fill={i < Math.round(r.rating) ? "#D4AF37" : "transparent"}
                        color="#D4AF37" />
                    ))}
                  </div>
                  <span className="text-sm text-[#F5F0E6]">{r.guest_name}</span>
                  {r.verified && (
                    <span className="text-[9px] uppercase tracking-[0.3em] px-1.5 py-0.5 rounded-full border border-[#8FBC8F]/60 text-[#8FBC8F]">
                      Ticket verifiziert
                    </span>
                  )}
                </div>
                <span className="text-[10px] text-[#575148] font-mono">
                  {r.created_at ? new Date(r.created_at).toLocaleDateString("de-DE") : ""}
                </span>
              </div>
              {r.comment && (
                <p className="mt-2 text-sm text-[#F5F0E6]/80 leading-relaxed">{r.comment}</p>
              )}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function Chip({ children, gold }) {
  return <span className={`px-3 py-1 rounded-full border ${gold ? "border-[#D4AF37] text-[#D4AF37]" : "border-[#2A2A2A] text-[#A8A398]"}`}>{children}</span>;
}
function Info({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.4em] text-[#575148]">{label}</div>
      <div className="mt-1">{value}</div>
    </div>
  );
}

function ClubSignupForm() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [newsletter, setNewsletter] = useState(true);
  const [club, setClub] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/landing/subscribe`, { name, email, newsletter, club });
      toast.success(data.member ? "Willkommen im Club!" : "Newsletter angemeldet!");
      setName(""); setEmail(""); setClub(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Anmeldung fehlgeschlagen");
    } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="flex flex-col md:flex-row items-stretch gap-2 max-w-xl mx-auto" data-testid="landing-club-form">
      <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Dein Name" data-testid="landing-name"
        className="flex-1 bg-[#050505] border border-[#2A2A2A] rounded px-4 py-3 outline-none focus:border-[#D4AF37]" />
      <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="E-Mail" required data-testid="landing-email"
        className="flex-1 bg-[#050505] border border-[#2A2A2A] rounded px-4 py-3 outline-none focus:border-[#D4AF37]" />
      <button type="submit" disabled={busy} data-testid="landing-signup-submit"
        className="bg-[#D4AF37] text-black rounded px-6 py-3 text-[11px] uppercase tracking-[0.4em] hover:bg-[#EEDC82] transition-colors disabled:opacity-40">
        <Mail size={12} className="inline mr-2" />{busy ? "…" : "Anmelden"}
      </button>
      <div className="md:col-span-3 flex flex-wrap gap-4 justify-center text-[10px] uppercase tracking-[0.3em] text-[#A8A398] mt-3">
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={newsletter} onChange={(e) => setNewsletter(e.target.checked)} className="accent-[#D4AF37]" /> Newsletter
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={club} onChange={(e) => setClub(e.target.checked)} className="accent-[#D4AF37]" /> Club-Karte gratis dazu
        </label>
      </div>
    </form>
  );
}

function EmptyBlock({ icon, text }) {
  return (
    <div className="mt-10 py-16 text-center border border-dashed border-[#2A2A2A] rounded-lg">
      <div className="text-[#575148] flex justify-center">{icon}</div>
      <div className="mt-4 text-[11px] uppercase tracking-[0.4em] text-[#A8A398]">{text}</div>
    </div>
  );
}


// ---- Marathon Order Modal -----------------------------------------------
function MarathonOrderModal({ series, onClose }) {
  const [form, setForm] = useState({ guest_name: "", email: "", phone: "", note: "", seat_count: 1 });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // { code, price, showtimes[] }

  const totalPrice = useMemo(() => (Number(series.marathon_price || 0) * Number(form.seat_count || 1)), [series.marathon_price, form.seat_count]);
  const qrValue = result ? `${window.location.origin}/marathon/${result.code}` : "";

  const submit = async (e) => {
    e.preventDefault();
    if (!form.guest_name.trim() || !form.email.trim()) {
      return toast.error("Bitte Name und E-Mail angeben");
    }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/marathon/order`, { ...form, series_id: series.id });
      setResult(data);
      toast.success("Marathon-Ticket erstellt! Prüfe dein Postfach.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bestellung fehlgeschlagen");
    } finally { setBusy(false); }
  };

  const payOnline = async () => {
    if (!form.guest_name.trim() || !form.email.trim()) {
      return toast.error("Bitte Name und E-Mail angeben");
    }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/marathon/stripe-checkout`, { ...form, series_id: series.id });
      if (data?.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        toast.error("Zahlung konnte nicht gestartet werden");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Zahlung konnte nicht gestartet werden");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/95 backdrop-blur-md z-[80] overflow-y-auto p-4" onClick={onClose} data-testid="marathon-order-modal">
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-full max-w-lg bg-[#0A0A0A] border border-[#D4AF37]/40 rounded-xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
          <div className="p-5 border-b border-[#1A1A1A] flex items-center gap-3">
            <Ticket size={22} className="text-[#D4AF37]" />
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-[0.4em] text-[#D4AF37]">Marathon-Ticket</div>
              <div className="font-heading text-xl text-[#F5F0E6]">{series.name}</div>
            </div>
            <button onClick={onClose} data-testid="marathon-order-close"><X size={20} className="text-[#A8A398]" /></button>
          </div>

          {!result ? (
            <form onSubmit={submit} className="p-5 space-y-4" data-testid="marathon-order-form">
              <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded p-3 text-sm text-[#EEDC82]">
                <b>1 QR-Code · alle {(series.movies || []).length || "Marathon-"}Filme</b><br/>
                <span className="text-xs text-[#A8A398]">Fixpreis {Number(series.marathon_price).toFixed(2)} € pro Person · Bezahlung im Kino</span>
              </div>

              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Name *</label>
                <input required value={form.guest_name} onChange={(e) => setForm({ ...form, guest_name: e.target.value })}
                  data-testid="marathon-order-name" placeholder="Vor- und Nachname"
                  className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2.5 focus:border-[#D4AF37] outline-none" />
              </div>

              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">E-Mail *</label>
                <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="marathon-order-email" placeholder="fuer.QR-Code@example.com"
                  className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2.5 focus:border-[#D4AF37] outline-none" />
                <div className="text-[10px] text-[#575148] mt-1">Wir senden das PDF-Ticket mit QR-Code an diese Adresse.</div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Telefon (optional)</label>
                  <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    data-testid="marathon-order-phone" placeholder="+49 …"
                    className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2.5 focus:border-[#D4AF37] outline-none" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Sitzplätze</label>
                  <input type="number" min="1" max="8" value={form.seat_count} onChange={(e) => setForm({ ...form, seat_count: Math.max(1, Math.min(8, Number(e.target.value) || 1)) })}
                    data-testid="marathon-order-seats"
                    className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2.5 focus:border-[#D4AF37] outline-none font-mono text-center" />
                </div>
              </div>

              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Notiz (optional)</label>
                <textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}
                  data-testid="marathon-order-note" placeholder="Allergien · Sitzwunsch · Barrierefreiheit …"
                  className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 text-sm focus:border-[#D4AF37] outline-none" />
              </div>

              <div className="flex items-center justify-between border-t border-[#1A1A1A] pt-4">
                <div className="text-[10px] uppercase tracking-widest text-[#A8A398]">Gesamt</div>
                <div className="font-heading text-2xl text-[#D4AF37]" data-testid="marathon-order-total">{totalPrice.toFixed(2)} €</div>
              </div>

              <div className="grid grid-cols-1 gap-2">
                <button type="button" onClick={payOnline} disabled={busy} data-testid="marathon-order-pay-online"
                  className="w-full py-3 bg-[#D4AF37] hover:bg-[#EEDC82] text-black rounded-md text-[11px] uppercase tracking-widest disabled:opacity-40">
                  {busy ? "…" : `Jetzt online bezahlen · ${totalPrice.toFixed(2)} €`}
                </button>
                <button type="submit" disabled={busy} data-testid="marathon-order-submit"
                  className="w-full py-2.5 bg-transparent border border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/10 rounded-md text-[10px] uppercase tracking-widest disabled:opacity-40">
                  {busy ? "…" : "Später an der Kasse zahlen"}
                </button>
              </div>
              <div className="text-[10px] text-center text-[#575148]">Sicher · Stripe · Rechnung + QR-Code sofort per E-Mail</div>
            </form>
          ) : (
            <div className="p-5 space-y-4 text-center" data-testid="marathon-order-success">
              <div className="text-[10px] uppercase tracking-[0.4em] text-[#8FBC8F]">Bestellung erfolgreich</div>
              <div className="text-lg text-[#F5F0E6]">Dein Marathon-Ticket ist reserviert.</div>
              <div className="bg-white p-4 rounded inline-block mx-auto">
                <QRCodeCanvas value={qrValue} size={180} bgColor="#FFFFFF" fgColor="#0A0A0A" />
              </div>
              <div className="font-mono text-lg text-[#D4AF37]" data-testid="marathon-order-code">{result.code}</div>
              <div className="text-xs text-[#A8A398]">
                {result.seat_count} Sitz(e) · <b className="text-[#D4AF37]">{Number(result.price).toFixed(2)} €</b> · Bezahlung im Kino
              </div>
              <div className="text-xs text-[#A8A398]">
                Wir haben dir zusätzlich eine <b className="text-[#EEDC82]">E-Mail mit PDF-Ticket</b> gesendet.
              </div>
              {(result.showtimes || []).length > 0 && (
                <div className="text-left border-t border-[#1A1A1A] pt-3">
                  <div className="text-[10px] uppercase tracking-widest text-[#A8A398] mb-2">Gültig für {result.showtimes.length} Vorstellungen</div>
                  <div className="space-y-1 max-h-40 overflow-y-auto">
                    {result.showtimes.map((s) => (
                      <div key={s.id} className="text-xs font-mono text-[#EEDC82]">
                        · {(s.date || "").split("-").reverse().join(".")} · {s.start_time} · {s.hall}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <button onClick={onClose} className="w-full py-2.5 border border-[#2A2A2A] rounded text-[11px] uppercase tracking-widest text-[#A8A398] hover:text-[#D4AF37] hover:border-[#D4AF37]">
                Schließen
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


// ---- Filmreihe Detail Modal ----------------------------------------------
function FilmreiheModal({ seriesId, onClose, onBookMarathon }) {
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!seriesId) return;
    setLoading(true);
    axios.get(`${API}/series/${seriesId}/public`)
      .then((r) => setS(r.data))
      .catch(() => toast.error("Filmreihe konnte nicht geladen werden"))
      .finally(() => setLoading(false));
  }, [seriesId]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [onClose]);

  const spotifyEmbed = useMemo(() => {
    const u = (s?.foyer_music_url || "").trim();
    if (!u) return null;
    const m = u.match(/open\.spotify\.com\/(?:intl-\w+\/)?(playlist|album|track|artist)\/([\w]+)/i);
    if (!m) return null;
    return `https://open.spotify.com/embed/${m[1]}/${m[2]}?utm_source=generator&theme=0`;
  }, [s?.foyer_music_url]);

  return (
    <div className="fixed inset-0 bg-black/90 backdrop-blur-md z-[70] overflow-y-auto" onClick={onClose} data-testid="filmreihe-modal">
      <div className="min-h-screen flex items-start justify-center p-4 md:p-8">
        <div className="w-full max-w-5xl bg-[#0A0A0A] border border-[#2A2A2A] rounded-xl overflow-hidden my-4"
          style={{ borderColor: (s?.color || "#D4AF37") + "60" }}
          onClick={(e) => e.stopPropagation()}>

          {/* Backdrop-Hero */}
          <div className="relative aspect-[21/9] bg-[#050505]">
            {(s?.backdrop_url || s?.poster_url) && (
              <img src={s.backdrop_url || s.poster_url} alt={s?.name} className="w-full h-full object-cover" />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/20" />
            <button onClick={onClose} data-testid="filmreihe-modal-close"
              className="absolute top-4 right-4 w-10 h-10 rounded-full bg-black/60 backdrop-blur border border-white/10 flex items-center justify-center text-[#F5F0E6] hover:bg-black/80 hover:text-[#D4AF37]">
              <X size={18} />
            </button>
            {s?.marathon_price != null && s.marathon_price > 0 && (
              <div className="absolute top-4 left-4 bg-[#D4AF37] text-black px-4 py-2 rounded-full text-sm font-heading shadow-2xl uppercase tracking-widest" data-testid="filmreihe-modal-marathon">
                Marathon-Ticket · {Number(s.marathon_price).toFixed(2)} €
              </div>
            )}
            <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10">
              <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.4em] font-heading" style={{ color: s?.color || "#D4AF37" }}>
                <Layers size={13} /> Filmreihe
              </div>
              <h2 className="font-heading text-3xl sm:text-4xl md:text-6xl text-[#F5F0E6] mt-2 leading-[0.95]">{s?.name || "…"}</h2>
              {s?.subtitle && <div className="text-sm md:text-lg text-[#A8A398] mt-2">{s.subtitle}</div>}
            </div>
          </div>

          {loading && <div className="p-10 text-center text-[#575148]"><Loader2 size={22} className="mx-auto animate-spin" /></div>}

          {!loading && s && (
            <div className="p-6 md:p-10 space-y-8">
              {s.description && (
                <p className="text-base md:text-lg text-[#EEDC82]/90 leading-relaxed italic border-l-2 pl-4" style={{ borderColor: s.color || "#D4AF37" }}>
                  {s.description}
                </p>
              )}

              {/* Marathon-Ticket-CTA — nur bei gesetztem Preis */}
              {s.marathon_price != null && s.marathon_price > 0 && (
                <div className="pt-2">
                  <button onClick={() => { onBookMarathon && onBookMarathon(s); onClose(); }} data-testid="filmreihe-modal-book-marathon"
                    className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-md text-sm font-heading tracking-widest uppercase transitions-safe"
                    style={{ background: s.color || "#D4AF37", color: "#000" }}>
                    <Ticket size={16} /> Marathon-Ticket buchen · {Number(s.marathon_price).toFixed(2)} € pro Person
                  </button>
                  <div className="text-[10px] text-center text-[#575148] mt-2">
                    1 QR · alle {(s.movies || []).length} Filme · gültig an allen Vorstellungen der Reihe
                  </div>
                </div>
              )}

              {/* Filme */}
              {(s.movies || []).length > 0 && (
                <div>
                  <h3 className="text-[10px] uppercase tracking-[0.4em] font-heading text-[#D4AF37] mb-4">Filme in dieser Reihe · {s.movies.length}</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="filmreihe-modal-movies">
                    {s.movies.map((m, i) => (
                      <div key={m.id} className="group" data-testid={`filmreihe-movie-${m.id}`}>
                        <div className="relative aspect-[2/3] bg-[#050505] rounded overflow-hidden border border-[#1A1A1A] hover:border-[#D4AF37]/60 transitions-safe">
                          {m.poster_url ? <img src={m.poster_url} alt={m.title_de} className="w-full h-full object-cover group-hover:scale-105 transition-transform" /> : null}
                          <div className="absolute top-1.5 left-1.5 bg-black/70 backdrop-blur px-1.5 py-0.5 rounded text-[9px] font-mono text-[#D4AF37]">
                            #{i + 1}
                          </div>
                        </div>
                        <div className="mt-2 text-xs text-[#F5F0E6] line-clamp-1">{m.title_de}</div>
                        {(m.year || m.duration_min) && (
                          <div className="text-[10px] text-[#575148] font-mono">
                            {m.year || ""}{m.year && m.duration_min ? " · " : ""}{m.duration_min ? `${m.duration_min} Min` : ""}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Kommende Vorstellungen */}
              {(s.upcoming_shows_list || []).length > 0 && (
                <div>
                  <h3 className="text-[10px] uppercase tracking-[0.4em] font-heading text-[#D4AF37] mb-4">Kommende Vorstellungen</h3>
                  <div className="grid md:grid-cols-2 gap-2" data-testid="filmreihe-modal-shows">
                    {s.upcoming_shows_list.map((sh) => (
                      <Link key={sh.id} to={`/kiosk/ticket/${sh.id}`}
                        className="group flex items-center gap-3 p-3 bg-[#050505] border border-[#1A1A1A] rounded hover:border-[#D4AF37]/60 transitions-safe"
                        data-testid={`filmreihe-show-${sh.id}`}>
                        {sh.movie?.poster_url && <img src={sh.movie.poster_url} alt="" className="w-10 h-14 object-cover rounded" />}
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-[#F5F0E6] truncate">{sh.movie?.title_de || "Film"}</div>
                          <div className="text-[10px] font-mono text-[#A8A398] mt-0.5">
                            {(sh.date || "").split("-").reverse().join(".")} · {sh.start_time} Uhr · {sh.hall}
                          </div>
                        </div>
                        <ArrowRight size={14} className="text-[#575148] group-hover:text-[#D4AF37] group-hover:translate-x-1 transitions-safe" />
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Spotify-Playlist Vorschau */}
              {spotifyEmbed && (
                <div>
                  <h3 className="text-[10px] uppercase tracking-[0.4em] font-heading text-[#1DB954] mb-3 flex items-center gap-2">
                    <span>🎵</span> Passende Musik im Foyer
                  </h3>
                  <iframe title="Spotify" src={spotifyEmbed} width="100%" height="152" frameBorder="0"
                    allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                    className="rounded-lg" data-testid="filmreihe-modal-spotify" />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
